// ./src/components/features/PlayListItemFormModal/PlayListItemFormModal.tsx
import React, { useState, useEffect, useMemo } from "react";
import {
  Stack,
  Text,
  Grid,
  Alert,
  InputLabel,
  NumberInput,
  Checkbox,
  Divider,
  Paper,
  Group,
  Title,
  Box,
} from "@mantine/core";
import { DateInput, TimeInput } from "@mantine/dates";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";

dayjs.extend(utc);

import { AppModal } from "@/components/generic/AppModal";
import {
  PlayListItem,
  PlayListItemInput,
} from "@/utils/api/PlayListItemService";
import { ContentBlock } from "@/utils/api/ContentBlockService";
import { AppIcon } from "@/components/generic/AppIcon";
import { StatusSelector, ContentBlockSelector } from "@/components/features";

import {
  RecurrenceSelector,
  RecurrenceDaysSelector,
} from "@/components/generic";

export interface PlayListItemFormModalProps {
  /** Whether the modal is open */
  opened: boolean;

  /** Function to close the modal */
  onClose: () => void;

  /** PlayList ID that this item belongs to */
  playListId: string;

  /** Venue ID for filtering content blocks */
  venueId?: string | null;

  /** PlayListItem to edit (if null/undefined, it's create mode) */
  playListItem?: PlayListItem | null;

  /** Callback when playlist item should be created */
  onCreatePlayListItem?: (
    itemData: PlayListItemInput
  ) => Promise<PlayListItem | null>;

  /** Callback when playlist item should be updated */
  onUpdatePlayListItem?: (
    itemId: string,
    itemData: PlayListItemInput
  ) => Promise<PlayListItem | null>;

  /** Whether create operation is in progress */
  isCreating?: boolean;

  /** Whether update operation is in progress */
  isUpdating?: boolean;

  /** Last error from operations */
  lastError?: string | null;

  /** Callback to clear errors */
  onClearError?: () => void;
}

interface PlayListItemFormData {
  contentBlockId: string | null;
  orderNo: number | undefined;
  status: number;
  // Override flags
  overrideDuration: boolean;
  overrideSchedule: boolean;
  overrideRecurrence: boolean;
  // Override values
  durationSeconds: number | null | undefined;
  startDatetime: string | null | undefined;
  endDatetime: string | null | undefined;
  startTime: string | null | undefined;
  endTime: string | null | undefined;
  recurrencePattern: string | null | undefined;
  recurrenceDays: string | null | undefined;
}

const initialFormData: PlayListItemFormData = {
  contentBlockId: null,
  orderNo: undefined,
  status: 100,
  overrideDuration: false,
  overrideSchedule: false,
  overrideRecurrence: false,
  durationSeconds: null,
  startDatetime: null,
  endDatetime: null,
  startTime: null,
  endTime: null,
  recurrencePattern: "NONE",
  recurrenceDays: null,
};

export const PlayListItemFormModal: React.FC<PlayListItemFormModalProps> = ({
  opened,
  onClose,
  playListId,
  playListItem,
  onCreatePlayListItem,
  onUpdatePlayListItem,
  isCreating = false,
  isUpdating = false,
  lastError,
  onClearError,
}) => {
  const [formData, setFormData] =
    useState<PlayListItemFormData>(initialFormData);
  const [selectedContentBlock, setSelectedContentBlock] =
    useState<ContentBlock | null>(null);
  const [validationErrors, setValidationErrors] = useState<
    Partial<Record<string, string>>
  >({});

  const isEditMode = Boolean(playListItem);
  const isSubmitting = isCreating || isUpdating;

  // Reset form when modal opens/closes or item changes
  useEffect(() => {
    if (opened) {
      if (playListItem) {
        // Edit mode - populate form with item data
        const hasOverrideDuration = playListItem.durationSeconds != null;
        const hasOverrideSchedule =
          playListItem.startDatetime != null ||
          playListItem.endDatetime != null ||
          playListItem.startTime != null ||
          playListItem.endTime != null;
        const hasOverrideRecurrence =
          playListItem.recurrencePattern != null ||
          playListItem.recurrenceDays != null;

        setFormData({
          contentBlockId: playListItem.contentBlockId || null,
          orderNo: playListItem.orderNo,
          status: playListItem.status != null ? playListItem.status : 100,
          overrideDuration: hasOverrideDuration,
          overrideSchedule: hasOverrideSchedule,
          overrideRecurrence: hasOverrideRecurrence,
          durationSeconds: playListItem.durationSeconds,
          startDatetime: playListItem.startDatetime
            ? dayjs(playListItem.startDatetime).format("YYYY-MM-DDTHH:mm:00Z")
            : null,
          endDatetime: playListItem.endDatetime
            ? dayjs(playListItem.endDatetime).format("YYYY-MM-DDTHH:mm:00Z")
            : null,
          startTime: playListItem.startTime
            ? dayjs(playListItem.startTime).format("HH:mm")
            : null,
          endTime: playListItem.endTime
            ? dayjs(playListItem.endTime).format("HH:mm")
            : null,
          recurrencePattern: playListItem.recurrencePattern || "NONE",
          recurrenceDays: playListItem.recurrenceDays || null,
        });

        // Set the selected content block if available
        if (playListItem.contentBlock) {
          setSelectedContentBlock(playListItem.contentBlock as ContentBlock);
        }
      } else {
        // Create mode - reset to initial values
        setFormData(initialFormData);
        setSelectedContentBlock(null);
      }
      setValidationErrors({});
      onClearError?.();
    }
  }, [opened, playListItem, onClearError]);

  // Get inherited values from content block
  const inheritedValues = useMemo(() => {
    if (!selectedContentBlock) {
      return {
        duration: null,
        startDatetime: null,
        endDatetime: null,
        startTime: null,
        endTime: null,
        recurrencePattern: null,
        recurrenceDays: null,
      };
    }

    return {
      duration: selectedContentBlock.durationSeconds,
      startDatetime: selectedContentBlock.startDatetime,
      endDatetime: selectedContentBlock.endDatetime,
      startTime: selectedContentBlock.startTime,
      endTime: selectedContentBlock.endTime,
      recurrencePattern: selectedContentBlock.recurrencePattern,
      recurrenceDays: selectedContentBlock.recurrenceDays,
    };
  }, [selectedContentBlock]);

  const validateForm = (): boolean => {
    const newErrors: Partial<Record<string, string>> = {};

    // Required fields
    if (!formData.contentBlockId) {
      newErrors.contentBlockId = "Content block is required";
    }

    // Validate schedule dates if overridden
    if (formData.overrideSchedule) {
      if (formData.startDatetime && formData.endDatetime) {
        if (formData.startDatetime >= formData.endDatetime) {
          newErrors.endDatetime = "End date must be after start date";
        }
      }

      if (formData.startTime && formData.endTime) {
        if (formData.startTime >= formData.endTime) {
          newErrors.endTime = "End time must be after start time";
        }
      }
    }

    // Validate recurrence days if overridden
    if (formData.overrideRecurrence) {
      if (
        formData.recurrencePattern === "WEEKLY" ||
        formData.recurrencePattern === "MONTHLY"
      ) {
        if (!formData.recurrenceDays || formData.recurrenceDays.trim() === "") {
          newErrors.recurrenceDays =
            "Recurrence days are required for weekly and monthly patterns";
        }
      }
    }

    setValidationErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (field: keyof PlayListItemFormData, value: any) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    // Clear validation error for this field
    if (validationErrors[field]) {
      setValidationErrors((prev) => ({
        ...prev,
        [field]: undefined,
      }));
    }
  };

  const handleContentBlockChange = (
    blockId: string | null,
    block?: ContentBlock
  ) => {
    handleInputChange("contentBlockId", blockId);
    setSelectedContentBlock(block || null);
  };

  const handleOverrideToggle = (
    field: "overrideDuration" | "overrideSchedule" | "overrideRecurrence"
  ) => {
    setFormData((prev) => {
      const newValue = !prev[field];
      const updates: Partial<PlayListItemFormData> = { [field]: newValue };

      // Clear override values when disabling override
      if (!newValue) {
        if (field === "overrideDuration") {
          updates.durationSeconds = null;
        } else if (field === "overrideSchedule") {
          updates.startDatetime = null;
          updates.endDatetime = null;
          updates.startTime = null;
          updates.endTime = null;
        } else if (field === "overrideRecurrence") {
          updates.recurrencePattern = "NONE";
          updates.recurrenceDays = null;
        }
      }

      return { ...prev, ...updates };
    });
  };

  const handleRecurrenceDaysChange = (values: string[]) => {
    const joinedValues = values.length > 0 ? values.join(",") : null;
    handleInputChange("recurrenceDays", joinedValues);
  };

  const getRecurrenceDaysValue = (): string[] => {
    if (!formData.recurrenceDays) return [];
    return formData.recurrenceDays.split(",").filter(Boolean);
  };

  const handleSubmit = async () => {
    onClearError?.();

    if (!validateForm()) {
      return;
    }

    try {
      // Prepare item data - only include overrides if enabled
      const itemData: PlayListItemInput = {
        playListId: playListId,
        contentBlockId: formData.contentBlockId || undefined,
        orderNo: formData.orderNo,
        status: formData.status,
        durationSeconds: formData.overrideDuration
          ? formData.durationSeconds
          : null,
        startDatetime: formData.overrideSchedule
          ? formData.startDatetime
            ? dayjs(formData.startDatetime).utc().format("YYYY-MM-DDTHH:mm:00Z")
            : null
          : null,
        endDatetime: formData.overrideSchedule
          ? formData.endDatetime
            ? dayjs(formData.endDatetime).utc().format("YYYY-MM-DDTHH:mm:00Z")
            : null
          : null,
        startTime: formData.overrideSchedule
          ? formData.startTime
            ? dayjs(`1970-01-01 ${formData.startTime}`)
                .utc()
                .format("YYYY-MM-DDTHH:mm:00Z")
            : null
          : null,
        endTime: formData.overrideSchedule
          ? formData.endTime
            ? dayjs(`1970-01-01 ${formData.endTime}`)
                .utc()
                .format("YYYY-MM-DDTHH:mm:00Z")
            : null
          : null,
        recurrencePattern: formData.overrideRecurrence
          ? formData.recurrencePattern === "NONE"
            ? null
            : formData.recurrencePattern
          : null,
        recurrenceDays: formData.overrideRecurrence
          ? formData.recurrenceDays
          : null,
      };

      let result: PlayListItem | null = null;

      if (isEditMode && playListItem && onUpdatePlayListItem) {
        result = await onUpdatePlayListItem(playListItem.id || "", itemData);
      } else if (!isEditMode && onCreatePlayListItem) {
        result = await onCreatePlayListItem(itemData);
      }

      if (result) {
        onClose();
      }
    } catch (error) {
      console.error("Form submission error:", error);
    }
  };

  const handleCancel = () => {
    if (!isSubmitting) {
      onClose();
    }
  };

  // Format duration for display
  const formatDuration = (seconds: number | null | undefined): string => {
    if (!seconds) return "Not set";
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return `${hours}h ${minutes}m ${secs}s`;
  };

  const renderGenericErrors = () => {
    if (!lastError) return null;

    return (
      <Alert
        color="red"
        icon={<AppIcon iconName="AlertCircle" size={16} />}
        title="Error"
      >
        <Text size="sm">{lastError}</Text>
      </Alert>
    );
  };

  const shouldShowRecurrenceDays =
    formData.recurrencePattern === "WEEKLY" ||
    formData.recurrencePattern === "MONTHLY";

  return (
    <AppModal
      opened={opened}
      onClose={handleCancel}
      title={isEditMode ? "Edit Playlist Item" : "Add Item to Playlist"}
      size="lg"
      loading={isSubmitting}
      actions={[
        {
          label: "Cancel",
          variant: "secondary",
          onClick: handleCancel,
          disabled: isSubmitting,
        },
        {
          label: "Save",
          variant: "primary",
          iconName: "Check",
          onClick: handleSubmit,
          inProgress: isSubmitting,
        },
      ]}
    >
      <Stack gap="md">
        {/* Content Block Selection */}
        <Grid>
          <Grid.Col span={12}>
            <ContentBlockSelector
              label="Content Block"
              value={formData.contentBlockId}
              onChange={handleContentBlockChange}
              //venueId={venueId}
              required
              disabled={isSubmitting}
              error={validationErrors.contentBlockId}
            />
          </Grid.Col>
        </Grid>

        <Divider />

        {/* Duration Override */}
        <Paper p="md" withBorder>
          <Stack gap="sm">
            <Group justify="space-between">
              <Title order={5}>Duration</Title>
              <Checkbox
                label="Override"
                checked={formData.overrideDuration}
                onChange={() => handleOverrideToggle("overrideDuration")}
                disabled={isSubmitting}
                size="md"
              />
            </Group>

            {!formData.overrideDuration ? (
              <Text size="sm" c="dimmed">
                Inherited: {formatDuration(inheritedValues.duration)}
              </Text>
            ) : (
              <NumberInput
                label="Duration (seconds)"
                placeholder="Enter duration in seconds"
                value={formData.durationSeconds || undefined}
                onChange={(value) =>
                  handleInputChange("durationSeconds", value || null)
                }
                min={1}
                step={1}
                disabled={isSubmitting}
                size="md"
              />
            )}
          </Stack>
        </Paper>

        {/* Schedule Override */}
        <Paper p="md" withBorder>
          <Stack gap="sm">
            <Group justify="space-between">
              <Title order={5}>Schedule</Title>
              <Checkbox
                label="Override"
                checked={formData.overrideSchedule}
                onChange={() => handleOverrideToggle("overrideSchedule")}
                disabled={isSubmitting}
                size="md"
              />
            </Group>

            {!formData.overrideSchedule ? (
              <Text size="sm" c="dimmed">
                Inherited from content block
              </Text>
            ) : (
              <Box>
                <Grid mb="sm">
                  <Grid.Col span={{ base: 12, sm: 6 }}>
                    <DateInput
                      label="Start Date"
                      placeholder="Select start date"
                      value={formData.startDatetime}
                      onChange={(value) =>
                        handleInputChange("startDatetime", value)
                      }
                      error={validationErrors.startDatetime}
                      clearable
                      disabled={isSubmitting}
                      size="md"
                    />
                  </Grid.Col>

                  <Grid.Col span={{ base: 12, sm: 6 }}>
                    <DateInput
                      label="End Date"
                      placeholder="Select end date"
                      value={formData.endDatetime}
                      onChange={(value) =>
                        handleInputChange("endDatetime", value)
                      }
                      error={validationErrors.endDatetime}
                      clearable
                      minDate={formData.startDatetime || undefined}
                      disabled={isSubmitting}
                      size="md"
                    />
                  </Grid.Col>
                </Grid>

                <Grid>
                  <Grid.Col span={{ base: 12, sm: 6 }}>
                    <TimeInput
                      label="Start Time"
                      placeholder="Select start time"
                      value={formData.startTime || ""}
                      onChange={(e) =>
                        handleInputChange("startTime", e.target.value || null)
                      }
                      error={validationErrors.startTime}
                      disabled={isSubmitting}
                      size="md"
                    />
                  </Grid.Col>

                  <Grid.Col span={{ base: 12, sm: 6 }}>
                    <TimeInput
                      label="End Time"
                      placeholder="Select end time"
                      value={formData.endTime || ""}
                      onChange={(e) =>
                        handleInputChange("endTime", e.target.value || null)
                      }
                      error={validationErrors.endTime}
                      disabled={isSubmitting}
                      size="md"
                    />
                  </Grid.Col>
                </Grid>
              </Box>
            )}
          </Stack>
        </Paper>

        {/* Recurrence Override */}
        <Paper p="md" withBorder>
          <Stack gap="sm">
            <Group justify="space-between">
              <Title order={5}>Recurrence</Title>
              <Checkbox
                label="Override"
                checked={formData.overrideRecurrence}
                onChange={() => handleOverrideToggle("overrideRecurrence")}
                disabled={isSubmitting}
                size="md"
              />
            </Group>

            {!formData.overrideRecurrence ? (
              <Text size="sm" c="dimmed">
                Inherited from content block
              </Text>
            ) : (
              <Box>
                <Grid>
                  <Grid.Col span={12}>
                    <RecurrenceSelector
                      label="Recurrence Pattern"
                      value={formData.recurrencePattern}
                      onChange={(value: any) =>
                        handleInputChange("recurrencePattern", value)
                      }
                      error={validationErrors.recurrencePattern}
                      disabled={isSubmitting}
                    />
                  </Grid.Col>
                </Grid>

                {shouldShowRecurrenceDays && (
                  <Grid mt="sm">
                    <Grid.Col span={12}>
                      <RecurrenceDaysSelector
                        recurrencePattern={formData.recurrencePattern}
                        value={getRecurrenceDaysValue()}
                        onChange={handleRecurrenceDaysChange}
                        error={validationErrors.recurrenceDays}
                        disabled={isSubmitting}
                      />
                    </Grid.Col>
                  </Grid>
                )}
              </Box>
            )}
          </Stack>
        </Paper>

        <Grid>
          <Grid.Col span={{ base: 12, sm: 12 }}>
            <Stack gap={0}>
              <InputLabel required>Status</InputLabel>
              <StatusSelector
                value={formData.status}
                onChange={(value) => handleInputChange("status", value)}
                required
                disabled={isSubmitting}
              />
            </Stack>
          </Grid.Col>
        </Grid>

        {/* Generic errors */}
        {renderGenericErrors()}
      </Stack>
    </AppModal>
  );
};
